import * as THREE from 'three';
import { racePose, LANE_WIDTH } from '@decathlon/shared';
import { box } from './blocky.js';
import { Athlete } from './athlete.js';
import { buildBillboards, type BoardInfo } from './billboards.js';
import { HighJumpArea, LongJumpArea } from './events/field.js';
import { ShotPutArea } from './events/throws.js';
import { laneZ, Stadium } from './stadium.js';
import type { AffiliatePayload } from '../screens/affiliate.js';

export type ViewMode = 'side' | 'behind' | 'tv';
export type CameraMode = ViewMode | 'menu' | 'result';
export const VIEW_MODES: { id: ViewMode; name: string; icon: string }[] = [
  { id: 'side', name: 'Side', icon: '📷' },
  { id: 'behind', name: 'Behind', icon: '🎥' },
  { id: 'tv', name: 'TV', icon: '🎬' },
];
const VIEW_KEY = 'decathlon.view';
export function loadView(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === 'side' || v === 'behind' || v === 'tv') return v;
  } catch {
    /* ignore */
  }
  return 'side';
}

export class GameScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly stadium = new Stadium();
  readonly field = { lj: new LongJumpArea(), hj: new HighJumpArea(), sp: new ShotPutArea() };
  /** When set, cameras look here instead of at the focused athlete (e.g. a shot in flight). */
  readonly hurdles = new THREE.Group();
  focusPoint: THREE.Vector3 | null = null;
  private athletes = new Map<string, Athlete>();
  private focusId: string | null = null;
  mode: CameraMode = 'menu';
  /** Fixed camera facing (radians, 0 = +X along the runways), or null to follow the athlete. */
  cameraYaw: number | null = null;
  view: ViewMode = loadView();
  reduceMotion = false;
  private clock = new THREE.Timer();
  private camPos = new THREE.Vector3();
  private camLook = new THREE.Vector3();
  private sun: THREE.DirectionalLight;
  private lastSpeed = 0;
  private boards = new THREE.Group();
  private boardPanels: THREE.Mesh[] = [];
  private raycaster = new THREE.Raycaster();
  onFrame: ((dt: number) => void) | null = null;
  /** Called when a hoarding is clicked. */
  onBoardClick: ((board: BoardInfo) => void) | null = null;

  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(
      Math.min(devicePixelRatio, matchMedia('(pointer: coarse)').matches ? 1.25 : 2),
    );
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(this.renderer.domElement);

    this.scene.background = new THREE.Color('#8fd3ff');
    this.scene.fog = new THREE.Fog('#8fd3ff', 160, 520);
    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 900);

    this.scene.add(new THREE.HemisphereLight('#bfe6ff', '#5ab84a', 0.9));
    this.sun = new THREE.DirectionalLight('#ffffff', 2.2);
    this.sun.position.set(40, 90, -60);
    this.sun.castShadow = true;
    const shadowSize = matchMedia('(pointer: coarse)').matches ? 1024 : 2048;
    this.sun.shadow.mapSize.set(shadowSize, shadowSize);
    const s = 60;
    Object.assign(this.sun.shadow.camera, {
      left: -s,
      right: s,
      top: s,
      bottom: -s,
      near: 1,
      far: 300,
    });
    this.sun.shadow.bias = -0.0005;
    this.scene.add(this.sun, this.sun.target);
    this.scene.add(
      this.stadium.group,
      this.field.lj.group,
      this.field.hj.group,
      this.field.sp.group,
      this.boards,
    );

    for (let lane = 0; lane < 6; lane++)
      for (let n = 0; n < 10; n++) {
        const p = racePose(13.72 + n * 9.14, lane, 110);
        const g = new THREE.Group();
        g.add(box(0.08, 0.1, LANE_WIDTH - 0.1, '#f5f5f5', 0, 1.067, 0, 0));
        for (const side of [-1, 1]) {
          g.add(box(0.06, 1, 0.06, '#1e88e5', 0, 0.5, side * 0.5, 0));
          g.add(box(0.5, 0.05, 0.06, '#1e88e5', 0.2, 0.025, side * 0.5, 0));
        }
        g.position.set(p.x, 0, p.z);
        this.hurdles.add(g);
      }
    this.hurdles.visible = false;
    this.scene.add(this.hurdles);
    this.resize();
    addEventListener('resize', () => this.resize());
    new ResizeObserver(() => this.resize()).observe(container);
    const canvas = this.renderer.domElement;
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.renderer.setAnimationLoop(null);
      const ui = document.getElementById('ui');
      if (ui) {
        const notice = document.createElement('div');
        notice.className = 'center';
        notice.textContent = 'Graphics connection lost. Reload to return to the stadium.';
        ui.replaceChildren(notice);
      }
    });
    canvas.addEventListener('click', (e) => {
      const b = this.pickBoard(e.clientX, e.clientY);
      if (b) this.onBoardClick?.(b);
    });
    let hoverTick = 0;
    canvas.addEventListener('pointermove', (e) => {
      if (++hoverTick % 4) return; // raycast at a quarter of the pointer rate
      canvas.style.cursor = this.pickBoard(e.clientX, e.clientY) ? 'pointer' : '';
    });
    this.renderer.setAnimationLoop(() => this.frame());
  }

  /** Provide the affiliate payload; hoardings are (re)built around the stadium. */
  setAds(ads: AffiliatePayload): void {
    this.scene.remove(this.boards);
    const built = buildBillboards(ads);
    this.boards = built.group;
    this.boardPanels = built.panels;
    this.scene.add(this.boards);
  }

  private pickBoard(clientX: number, clientY: number): BoardInfo | null {
    if (!this.boardPanels.length) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    const hit = this.raycaster.intersectObjects(this.boardPanels, false)[0];
    const board = hit ? ((hit.object.userData as { board: BoardInfo }).board ?? null) : null;
    return board && board.kind !== 'house' ? board : null;
  }

  // ------------------------------------------------------------ athletes
  addAthlete(id: string, kit: string, lane: number): Athlete {
    let a = this.athletes.get(id);
    if (!a) {
      a = new Athlete(kit);
      this.athletes.set(id, a);
      this.scene.add(a.root);
    } else a.setKit(kit);
    a.place(0, laneZ(lane));
    return a;
  }

  getAthlete(id: string): Athlete | undefined {
    return this.athletes.get(id);
  }

  removeAthlete(id: string): void {
    const a = this.athletes.get(id);
    if (!a) return;
    this.scene.remove(a.root);
    this.athletes.delete(id);
  }

  clearAthletes(): void {
    for (const id of this.athletes.keys()) this.removeAthlete(id);
  }

  setFocus(id: string | null): void {
    this.focusId = id;
  }

  /** Speed of the focused athlete, for camera FOV; set by the app each frame. */
  setFocusSpeed(v: number): void {
    this.lastSpeed = v;
  }

  race(): void {
    this.mode = this.view;
  }

  setView(v: ViewMode): void {
    this.view = v;
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* ignore */
    }
    if (this.mode === 'side' || this.mode === 'behind' || this.mode === 'tv') this.mode = v;
  }

  cycleView(): ViewMode {
    const i = VIEW_MODES.findIndex((m) => m.id === this.view);
    const next = VIEW_MODES[(i + 1) % VIEW_MODES.length]!.id;
    this.setView(next);
    return next;
  }

  // ------------------------------------------------------------ loop
  private lastW = 0;
  private lastH = 0;
  private profile = new URLSearchParams(location.search).has('profile');
  private frameSamples: number[] = [];
  private profileEl: HTMLElement | null = null;
  private resize(): void {
    const w = this.renderer.domElement.parentElement?.clientWidth || innerWidth;
    const h = this.renderer.domElement.parentElement?.clientHeight || innerHeight;
    if (w === 0 || h === 0) return;
    this.lastW = this.renderer.domElement.clientWidth;
    this.lastH = this.renderer.domElement.clientHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private frame(): void {
    this.clock.update();
    const elapsed = this.clock.getDelta();
    const dt = Math.min(0.1, elapsed);
    if (this.profile) {
      this.frameSamples.push(elapsed * 1000);
      if (this.frameSamples.length >= 120) {
        if (!this.profileEl) {
          this.profileEl = document.createElement('output');
          this.profileEl.id = 'render-profile';
          this.profileEl.style.cssText =
            'position:fixed;bottom:0;left:0;background:#fff;color:#111;z-index:1000;padding:4px;font:12px monospace;pointer-events:none';
          document.body.append(this.profileEl);
        }
        const sorted = [...this.frameSamples].sort((a, b) => a - b);
        this.profileEl.textContent = `Frames: median ${sorted[60]!.toFixed(1)} ms, p95 ${sorted[114]!.toFixed(1)} ms; draw calls ${this.renderer.info.render.calls}; triangles ${this.renderer.info.render.triangles}`;
        this.frameSamples = [];
      }
    }
    // Belt and braces: ResizeObserver can miss a pane transition; check the CSS size each frame.
    const el = this.renderer.domElement;
    if (el.clientWidth !== this.lastW || el.clientHeight !== this.lastH) this.resize();
    this.onFrame?.(dt);
    this.stadium.update(this.reduceMotion ? 0 : dt);
    this.updateCamera(dt);
    this.renderer.render(this.scene, this.camera);
  }

  private updateCamera(dt: number): void {
    const k = 1 - Math.exp(-dt * 6);
    const t = performance.now() / 1000;
    const focus =
      (this.focusId && this.athletes.get(this.focusId)) || [...this.athletes.values()][0];
    const p = this.focusPoint ?? (focus ? focus.root.position : new THREE.Vector3(0, 0, laneZ(3)));
    // Direction of travel and the infield side (runners' left), so cameras follow round bends.
    // Cameras face the way the athlete faces (needed on the oval), unless an event pins the
    // facing: a discus thrower spins, and the camera must not spin with him.
    const yaw = this.cameraYaw ?? (focus ? focus.root.rotation.y : 0);
    const fx = Math.cos(yaw);
    const fz = -Math.sin(yaw);
    const lx = -Math.sin(yaw);
    const lz = -Math.cos(yaw);
    let fov = 55;
    let rate = k;
    if (this.mode === 'menu' || this.mode === 'result') {
      // Slow orbit. Look a little to the camera's left so the athlete sits right of centre,
      // clear of the menu and result panels.
      // Swing on the infield side only: a full orbit would carry the camera behind the
      // grandstand hoardings and show their backs.
      const swing = this.reduceMotion
        ? 0
        : Math.sin(t * (this.mode === 'menu' ? 0.15 : 0.25)) * 0.9;
      const a = Math.PI + swing;
      const r = this.mode === 'menu' ? 9 : 7;
      this.camPos.set(p.x + Math.sin(a) * r, 2.6, p.z + Math.cos(a) * r);
      const side = this.mode === 'menu' ? 4.5 : 3.5;
      this.camLook.set(p.x - Math.cos(a) * side, 1.1 + p.y * 0.5, p.z + Math.sin(a) * side);
      fov = 45;
    } else if (this.mode === 'side') {
      // Infield side, slightly ahead so the runner sits left of centre with the track visible.
      const ahead = 3.5 * Math.min(1, this.camera.aspect);
      this.camPos.set(p.x + fx * ahead + lx * 12, 2.4 + p.y * 0.3, p.z + fz * ahead + lz * 12);
      this.camLook.set(p.x + fx * ahead - lx * 3, 1.1 + p.y * 0.6, p.z + fz * ahead - lz * 3);
      fov = this.reduceMotion ? 50 : 50 + Math.min(1, this.lastSpeed / 11) * 8;
      rate = 1 - Math.exp(-dt * 14); // tight follow on the running axis
    } else if (this.mode === 'behind') {
      this.camPos.set(p.x - fx * 8 - lx * 0.6, 3.6 + p.y * 0.5, p.z - fz * 8 - lz * 0.6);
      this.camLook.set(p.x + fx * 8, 1.0 + p.y * 0.8, p.z + fz * 8);
      fov = this.reduceMotion ? 60 : 60 + Math.min(1, this.lastSpeed / 11) * 14;
      rate = 1 - Math.exp(-dt * 10);
    } else if (this.mode === 'tv') {
      // Fixed TV position past the finish on the infield side; it pans and zooms.
      this.camPos.set(112, 7, -26);
      this.camLook.set(p.x, 1.2 + p.y, p.z);
      const dist = this.camPos.distanceTo(this.camLook);
      fov = Math.max(14, Math.min(50, 900 / dist));
      rate = 1 - Math.exp(-dt * 4);
    }
    this.camera.position.lerp(this.camPos, rate);
    this.camera.fov += (fov - this.camera.fov) * k;
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.camLook);
    this.sun.position.copy(this.camLook).add(new THREE.Vector3(40, 90, -60));
    this.sun.target.position.copy(this.camLook);
  }
}
